<script setup lang="ts">
import {
    VTabs, VTab, VTabsWindow, VTabsWindowItem,
} from 'vuetify/components';
import { useValidationErrorStore } from '@/stores/validationErrorStore';
import { computed, watch } from 'vue';
import { BIcon } from '@/design-system';

type TabType = {
    id: string;
    title: string;
    disabled?: boolean;
};

type PropsType = {
    tabs: TabType[];
    color?: string;
    density?: VTabs['$props']['density'];
    grow?: boolean;
    vertical?: boolean;
    tabsContentClasses?: string;
    tabErrors?: Record<string, boolean | string>;
    panelIndex?: number;
    validation?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    color: 'primary',
    density: 'comfortable',
    grow: false,
    vertical: false,
});

const activeTab = defineModel<string>('modelValue', { default: '' });
const validationStore = useValidationErrorStore();

if (!activeTab.value && props.tabs.length > 0) {
    activeTab.value = props.tabs[0].id;
}

watch(activeTab, newTab => {
    validationStore.setCurrentTab(newTab);
});

const computedTabErrors = computed(() => {
    if (props.tabErrors) {
        return props.tabErrors;
    }

    if (props.validation && validationStore.currentFormId) {
        const tabErrors = validationStore.getTabErrors(validationStore.currentFormId, props.panelIndex);

        return tabErrors;
    }

    return {};
});

defineOptions({ inheritAttrs: false });
</script>

<template>
    <div
        class="b-tabs"
        :class="{ 'b-tabs--vertical': vertical }"
    >
        <div class="b-tabs__header">
            <VTabs
                v-model="activeTab"
                :color="color"
                :density="density"
                :grow="grow"
                :direction="vertical ? 'vertical' : 'horizontal'"
                class="b-tabs__nav"
                :class="{ 'b-tabs__nav--vertical': vertical }"
                v-bind="$attrs"
            >
                <VTab
                    v-for="tab in tabs"
                    :key="tab.id"
                    :value="tab.id"
                    :disabled="tab.disabled"
                    density="comfortable"
                    class="b-tabs__tab"
                >
                    <div class="b-tabs__tab-content">
                        <span class="b-tabs__tab-title">{{ tab.title }}</span>
                        <BIcon
                            v-if="computedTabErrors[tab.id]"
                            icon="mdi-alert-circle"
                            size="small"
                            color="error"
                            class="b-tabs__tab-error-icon"
                        />
                    </div>
                </VTab>
            </VTabs>

            <div
                v-if="$slots.append"
                class="b-tabs__append"
            >
                <slot name="append" />
            </div>
        </div>

        <VTabsWindow
            v-model="activeTab"
            class="b-tabs__content"
            :class="tabsContentClasses"
        >
            <VTabsWindowItem
                v-for="tab in tabs"
                :key="tab.id"
                :value="tab.id"
                class="b-tabs__window-item"
                eager
            >
                <slot
                    :name="tab.id"
                    :tab="tab"
                    :active="activeTab === tab.id"
                />
            </VTabsWindowItem>
        </VTabsWindow>
    </div>
</template>

<style lang="scss" scoped>
.v-tabs--horizontal:not(.v-tabs-pill) {
    border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    :deep(.v-tab__slider) {
        block-size: 3px;
    }
}
:deep(.v-tabs .v-btn .v-btn__overlay),
:deep(.v-tabs .v-btn .v-ripple__container) {
    opacity: 0;
}
.v-tabs .v-btn:hover {
    color: rgb(var(--v-theme-primary));
}

.b-tabs__tab {
    &--error {
        .b-tabs__tab-title {
            color: rgb(var(--v-theme-error));
        }
    }
}

.b-tabs__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.b-tabs__nav {
    flex: 1;
}

.b-tabs__append {
    flex-shrink: 0;
    margin-left: 16px;
}

.b-tabs__tab-content {
    display: flex;
    align-items: center;
    gap: 8px;
}
</style>
